import { API_BASE_URL, getAuthHeaders } from './api';

export const MAX_TOOL_INGESTION_GROUPS = 5;

/** Shared parser for tools and prompts. */
export function parseIngestionGroupSlugs(record) {
  return parseToolIngestionGroupSlugs(record);
}

export function parseToolIngestionGroupSlugs(toolOrForm) {
  const src = toolOrForm || {};
  if (Array.isArray(src.ingestion_group_slugs)) {
    return src.ingestion_group_slugs.map((s) => String(s || '').trim()).filter(Boolean).slice(0, MAX_TOOL_INGESTION_GROUPS);
  }
  if (Array.isArray(src.metadata?.group_slugs)) {
    return src.metadata.group_slugs.map((s) => String(s || '').trim()).filter(Boolean).slice(0, MAX_TOOL_INGESTION_GROUPS);
  }
  const single = String(src.ingestion_group_slug || src.metadata?.group_slug || '').trim();
  return single ? [single] : [];
}

function buildSearchOptionsFromKeysPayload(data) {
  const raw = Array.isArray(data?.raw) ? data.raw : [];
  const filesFile = Array.isArray(data?.files?.file) ? data.files.file : [];
  const filesText = Array.isArray(data?.files?.text) ? data.files.text : [];
  const intrinsicExisting = Array.isArray(data?.intrinsic?.existing) ? data.intrinsic.existing : [];
  const intrinsicDefined = Array.isArray(data?.intrinsic?.defined) ? data.intrinsic.defined : [];
  const outExisting = Array.isArray(data?.output?.existing) ? data.output.existing : [];
  const outDefined = Array.isArray(data?.output?.defined) ? data.output.defined : [];
  const outUnion = Array.from(new Set([...outExisting, ...outDefined].filter(Boolean)));
  const intrinsicUnion = Array.from(new Set([...intrinsicExisting, ...intrinsicDefined].filter(Boolean)));
  const rawPrefixed = Array.from(new Set(raw.filter(Boolean).map((k) => `raw.${k}`)));
  const fileAll = Array.from(new Set([...filesFile, ...filesText].filter(Boolean)));
  const intrinsicPrefixed = intrinsicUnion.map((k) => `intrinsic.${k}`);
  const searchOptions = Array.from(new Set([...rawPrefixed, ...fileAll, ...outUnion, ...intrinsicPrefixed]));
  const returnOptions = Array.from(new Set([...rawPrefixed, ...outUnion, ...fileAll, ...intrinsicPrefixed]));
  const fileKeysConvertToText = Array.from(new Set((filesFile || []).filter(Boolean)));
  return { searchOptions, returnOptions, fileKeysConvertToText };
}

export async function fetchUnifiedKeysForGroup(suffix, groupSlug) {
  const url = `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups/${encodeURIComponent(groupSlug)}/keys`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok || json?.success === false) {
    throw new Error(json?.error || 'Failed to load unified keys');
  }
  const built = buildSearchOptionsFromKeysPayload(json?.data || {});
  const ig = json?.data?.ingestion_group;
  return {
    groupSlug,
    ...built,
    meta: ig && typeof ig === 'object'
      ? { name: ig.name || null, is_private: ig.is_private !== false }
      : null,
  };
}

export function intersectFieldLists(lists) {
  if (!lists.length) return [];
  const [first, ...rest] = lists;
  const set = new Set(first);
  for (const list of rest) {
    const keep = new Set(list);
    for (const item of set) {
      if (!keep.has(item)) set.delete(item);
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function detectLocalFieldCollisions(groupSlugs, keysByGroup, { search_field, return_fields }) {
  const groups = (groupSlugs || []).map((s) => String(s || '').trim()).filter(Boolean);
  const fieldList = [];
  const sf = String(search_field || '').trim();
  if (sf) fieldList.push({ role: 'search_field', key: sf });
  for (const rf of Array.isArray(return_fields) ? return_fields : []) {
    const k = String(rf || '').trim();
    if (k) fieldList.push({ role: 'return_fields', key: k });
  }
  const collisions = [];
  const pairs = [];
  for (const { role, key } of fieldList) {
    const missingIn = groups.filter((g) => !keysByGroup[g]?.has(key));
    if (missingIn.length) collisions.push({ role, key, missingInGroups: missingIn });
  }
  for (let i = 0; i < groups.length; i += 1) {
    for (let j = i + 1; j < groups.length; j += 1) {
      const a = groups[i];
      const b = groups[j];
      const disputed = fieldList
        .filter(({ key }) => {
          const inA = keysByGroup[a]?.has(key);
          const inB = keysByGroup[b]?.has(key);
          return Boolean(inA) !== Boolean(inB);
        })
        .map((x) => x.key);
      if (disputed.length) pairs.push({ groupA: a, groupB: b, fields: disputed });
    }
  }
  return { collisions, pairs, hasCollisions: collisions.length > 0 || pairs.length > 0 };
}

export async function checkToolFieldCollisionsRemote({
  suffix,
  ingestion_group_slugs,
  search_field,
  return_fields,
}) {
  const headers = await getAuthHeaders();
  const resp = await fetch(`${API_BASE_URL}/casc/v1/api/vector/tools/check-field-collisions`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      suffix,
      ingestion_group_slugs,
      group_slugs: ingestion_group_slugs,
      search_field,
      return_fields,
    }),
  });
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    throw new Error(json?.error || 'Failed to check field collisions');
  }
  return json;
}

/** Detect { raw.x } / { file.x } placeholders not present in all groups' key sets. */
export function detectPromptTemplateCollisions(promptText, groupSlugs, keysByGroup) {
  const text = String(promptText || '');
  const re = /\{\s*([^}]+?)\s*\}/g;
  const fields = [];
  let m;
  while ((m = re.exec(text)) !== null) {
    const inner = String(m[1] || '').trim();
    if (!inner || inner.startsWith('{')) continue;
    if (inner.startsWith('raw.') || inner.startsWith('file.') || inner.startsWith('intrinsic.')) {
      fields.push({ role: 'prompt_template', key: inner });
    }
  }
  const collisions = [];
  const pairs = [];
  for (const { role, key } of fields) {
    const missingIn = (groupSlugs || []).filter((g) => !keysByGroup[g]?.has(key));
    if (missingIn.length) collisions.push({ role, key, missingInGroups: missingIn });
  }
  for (let i = 0; i < (groupSlugs || []).length; i += 1) {
    for (let j = i + 1; j < groupSlugs.length; j += 1) {
      const a = groupSlugs[i];
      const b = groupSlugs[j];
      const disputed = fields
        .filter(({ key }) => {
          const inA = keysByGroup[a]?.has(key);
          const inB = keysByGroup[b]?.has(key);
          return Boolean(inA) !== Boolean(inB);
        })
        .map((x) => x.key);
      if (disputed.length) pairs.push({ groupA: a, groupB: b, fields: disputed });
    }
  }
  return {
    collisions,
    pairs,
    hasCollisions: collisions.length > 0 || pairs.length > 0,
  };
}

export function formatCollisionSummary({ collisions, pairs }) {
  const lines = [];
  for (const c of collisions || []) {
    lines.push(
      `“${c.key}” (${c.role}) is missing in: ${(c.missingInGroups || []).join(', ')}`
    );
  }
  for (const p of pairs || []) {
    lines.push(
      `Groups “${p.groupA}” and “${p.groupB}” disagree on: ${(p.fields || []).join(', ')}`
    );
  }
  return lines;
}
